import { useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "@phosphor-icons/react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Popover, PopoverContent, PopoverTrigger } from "@libs/zapi-ui/ds/components/popover";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { MAX_PINNED_WIDGETS, type TPinnable } from "@libs/rqa-sim-widgets";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { BODY_2, BODY_3, ROBOTO_MONO } from "./_tokens";

const MAX_SUGGESTIONS = 5;
const DROPDOWN_MAX = 220;
const VIEWPORT_PAD = 8;
const MAX_PROMPT = 500;
const MIN_PROMPT = 3;

type TTwinPinProps = {
  options: TPinnable[];
  pending?: boolean;
  disabled?: boolean;
  full?: boolean;
  onPick: (option: TPinnable) => void;
  onDescribe: (prompt: string) => void;
};

export function TwinPin({ options, pending, disabled, full, onPick, onDescribe }: TTwinPinProps) {
  const { t } = useTranslation(["projectHome", "common"]);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [dropUp, setDropUp] = useState<boolean | null>(null);

  const suggestions = useMemo(() => {
    const needle = text.trim().toLowerCase();
    if (needle.length < 2) return [];
    return options
      .filter((option) => [option.name, option.key].some((field) => field.toLowerCase().includes(needle)))
      .slice(0, MAX_SUGGESTIONS);
  }, [options, text]);

  useLayoutEffect(() => {
    const field = inputRef.current;
    if (!field || suggestions.length === 0 || dropUp !== null) return;
    const below = window.innerHeight - field.getBoundingClientRect().bottom - VIEWPORT_PAD;
    setDropUp(below < DROPDOWN_MAX);
  }, [suggestions.length, dropUp]);

  const close = () => {
    setDropUp(null);
    setOpen(false);
    setText("");
  };
  const submit = () => {
    const prompt = text.trim();
    if (prompt.length < MIN_PROMPT) return;
    onDescribe(prompt);
    close();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };
  const title = pending ? t("rqaSim.describing") : t("rqaSim.addWidget");

  return (
    <Popover open={open && !pending} onOpenChange={(next) => (next ? setOpen(true) : close())}>
      <PopoverTrigger
        render={
          <Button disabled={disabled || pending} data-testid="rqa-sim-pin-open" className="rounded-full" />
        }
      >
        {pending ? <Spinner data-icon="inline-start" aria-label={t("common:loading")} /> : <Plus weight="bold" />}
        {title}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={8}
        initialFocus={inputRef}
        className="flex w-[420px] flex-col gap-[10px] overflow-visible rounded-2xl border-0 bg-popover p-2 shadow-lg ring-1 ring-foreground/10"
        data-testid="rqa-sim-pin-popover"
      >
        <div className="flex flex-col gap-1 px-3 py-2">
          <div className="flex h-5 items-center">
            <span className="typo-label-lg text-foreground">{t("rqaSim.describeWidget")}</span>
          </div>
          <p className="typo-caption text-muted-foreground">{t("rqaSim.describeHint")}</p>
        </div>
        {full ? (
          <p className={cn(BODY_3, "px-3 pb-2 text-innoveraZinc-500")}>
            {t("rqaSim.pinFull", { count: MAX_PINNED_WIDGETS })}
          </p>
        ) : (
          <>
            <div className="relative">
              <textarea
                ref={inputRef}
                value={text}
                maxLength={MAX_PROMPT}
                rows={4}
                onChange={(event) => setText(event.target.value)}
                onKeyDown={onKeyDown}
                placeholder={t("rqaSim.describePlaceholder")}
                data-testid="rqa-sim-describe-input"
                className={cn(
                  BODY_2,
                  "min-h-[120px] w-full resize-none rounded-xl border border-innoveraZinc-150 bg-white px-3 py-2 text-innoveraZinc-900 outline-hidden placeholder:text-innoveraZinc-400 focus:border-primary"
                )}
              />
              {suggestions.length > 0 && (
                <div
                  className={cn(
                    "absolute left-0 right-0 z-10 flex flex-col gap-1 overflow-y-auto rounded-lg border border-innoveraZinc-150 bg-white p-1 shadow-md",
                    dropUp ? "bottom-full mb-1" : "top-full mt-1"
                  )}
                  style={{ maxHeight: DROPDOWN_MAX }}
                  data-testid="rqa-sim-pin-suggestions"
                  data-side={dropUp ? "top" : "bottom"}
                >
                  <span className={cn(BODY_3, "px-2 pt-1 text-innoveraZinc-500")}>{t("rqaSim.pinInstead")}</span>
                  {suggestions.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      data-testid={`rqa-sim-pin-option-${option.key}`}
                      onClick={() => {
                        onPick(option);
                        close();
                      }}
                      className="flex w-full items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-left hover:bg-innoveraZinc-50"
                    >
                      <span className={cn(BODY_2, "min-w-0 truncate text-innoveraZinc-900")}>{option.name}</span>
                      <span className={cn(ROBOTO_MONO, "shrink-0 text-[12px] leading-[18px] text-innoveraZinc-500")}>
                        {option.key}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end px-3 pb-2">
              <Button
                onClick={submit}
                disabled={text.trim().length < MIN_PROMPT}
                data-testid="rqa-sim-describe-submit"
                className="rounded-full"
              >
                {t("rqaSim.addWidget")}
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
